<template>
  <div>
    <ui-dialog
      :show="isOpen"
      max-width="867"
      @input="handleClose"
      class="reply-templates"
    >
      <template #header>
        <div class="reply-templates__header">
          <h3>
            {{ $t("message.reviews.replyTemplates.title") }}
          </h3>
        </div>
      </template>

      <div class="d-flex align-center justify-space-between mb-4">
        <ui-button @click="handleClose" outlined icon="mdi-chevron-left">
          {{ $t("message.reviews.replyTemplates.templates.backToList") }}
        </ui-button>

        <div class="reply-templates__category" v-if="category">
          <p>
            {{ `${$t("message.reviews.replyTemplates.templates.category")}:` }}
          </p>
          <p>{{ category.name }}</p>
        </div>

        <ui-button
          @click="initAddTemplate"
          :disabled="loadingList"
          color="primary"
          outlined
          icon="icon-plus"
        >
          {{
            $t(
              `message.reviews.replyTemplates.templates.${DIALOG_MANAGE_MODE.ADD}`
            )
          }}
        </ui-button>

        <!-- <div>TODO: pagination</div> -->
      </div>

      <div class="reply-templates__body">
        <div
          v-if="templates && templates.length === 0"
          class="reply-templates__empty"
        >
          <p>
            {{ $t("message.reviews.replyTemplates.templates.noTemplates") }}
          </p>
        </div>

        <div v-else-if="templates">
          <v-list class="reply-templates__list">
            <ReplyTemplatesTemplateListItem
              v-for="template in templates"
              :key="template.id"
              :template="template"
              @edit="initEditTemplate"
              @delete="deleteTemplate"
            />
          </v-list>
        </div>

        <SectionLoader :status="loadingList" :opacity="0.75" />
      </div>
    </ui-dialog>
    <ReplyTemplatesManageTemplateDialog
      :is-open="dialog.manageTemplate"
      :template-to-edit="templateToEdit"
      :mode="manageTemplateMode"
      @close="closeManageTemplateDialog"
      @save="saveTemplate"
      @edit="editTemplate"
    />
  </div>
</template>

<script>
import SectionLoader from "@/components/Loader/SectionLoader";
import ReplyTemplatesManageTemplateDialog from "@/components/Reviews/ReplyTemplates/ManageTemplateDialog";
import ReplyTemplatesTemplateListItem from "@/components/Reviews/ReplyTemplates/TemplateListItem";

import { DIALOG_MANAGE_MODE } from "@/enums/reply-templates";
import { GTM_EVENT_NAME } from "@/enums/gtm-events";
import { gtmEvent__reviewTemplateManageAction } from "@/helpers/gtm-events";

import serviceHandler from "@/helpers/handlers/service";
import reviewsService from "@/services/reviews";

export default {
  name: "ReplyTemplatesDialog",
  components: {
    SectionLoader,
    ReplyTemplatesManageTemplateDialog,
    ReplyTemplatesTemplateListItem
  },

  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    category: {
      validator: prop => typeof prop === "object" || prop === null,
      required: true
    }
  },

  data() {
    return {
      DIALOG_MANAGE_MODE,
      dialog: {
        manageTemplate: false
      },
      manageTemplateMode: null,
      templateToEdit: null,
      loadingList: false,
      templates: null
    };
  },

  watch: {
    async isOpen(newValue) {
      if (newValue) {
        this.loadingList = true;
        await this.fetchReplyTemplates();
        this.loadingList = false;
      } else {
        this.templates = null;
      }
    }
  },

  methods: {
    async fetchReplyTemplates() {
      this.templates = await serviceHandler(
        reviewsService.getReplyTemplates({ categoryIds: [this.category.id] })
      );
    },

    initAddTemplate() {
      this.templateToEdit = null;
      this.manageTemplateMode = DIALOG_MANAGE_MODE.ADD;
      this.dialog.manageTemplate = true;
    },

    initEditTemplate(template) {
      this.templateToEdit = template;
      this.manageTemplateMode = DIALOG_MANAGE_MODE.EDIT;
      this.dialog.manageTemplate = true;
    },

    async saveTemplate(templateText) {
      this.loadingList = true;

      const payload = {
        category: this.category.id,
        text: templateText
      };

      const response = await serviceHandler(
        reviewsService.postReplyTemplate(payload)
      );

      if (response) {
        await this.fetchReplyTemplates();
        gtmEvent__reviewTemplateManageAction({ action: "template_add" });
        this.$gtm.trackCustomEvent({
          event: GTM_EVENT_NAME.REVIEW_TEMPLATE_MANAGE_ADD
        });
      }
      this.loadingList = false;
    },

    async editTemplate(template) {
      this.loadingList = true;

      const response = await serviceHandler(
        reviewsService.patchReplyTemplate(template)
      );

      if (response) {
        await this.fetchReplyTemplates();
        gtmEvent__reviewTemplateManageAction({ action: "template_edit" });
      }

      this.templateToEdit = null;
      this.manageTemplateMode = null;
      this.loadingList = false;
    },

    async deleteTemplate(category) {
      this.loadingList = true;

      await serviceHandler(reviewsService.deleteReplyTemplate(category));
      await this.fetchReplyTemplates();
      gtmEvent__reviewTemplateManageAction({ action: "template_remove" });

      this.loadingList = false;
    },

    closeManageTemplateDialog() {
      this.dialog.manageTemplate = false;
      this.manageTemplateMode = null;
    },

    handleClose() {
      this.$emit("close");
    }
  }
};
</script>

<style lang="scss" scoped>
.reply-templates {
  &__header {
    flex: 1 1 auto;
    padding-bottom: $spacing * 2.5;
    border-bottom: 1px solid $light-3;
  }

  &__category {
    display: flex;
    align-items: center;
    color: $text;

    p {
      margin-bottom: 0;

      &:first-child {
        opacity: 0.6;
        margin-right: $spacing;
        @include font-size(12px);
      }
    }
  }

  &__body {
    min-height: 140px;
    position: relative;
  }

  &__empty {
    min-height: 140px;
    margin-bottom: $spacing * 2;
    background-color: $light-4;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 25px;

    p {
      color: $text;
      text-align: center;
      max-width: 70%;
      white-space: pre-line;
      font-weight: 500;
      margin-bottom: 0;
    }
  }

  &__list {
    background-color: $light-4;
    padding: $spacing $spacing * 3;
    margin-bottom: $spacing * 2;
    border-radius: 25px;
    list-style: none;
    overflow-y: auto;
  }
}
</style>
